// === INPACT:SLUITSTRAAT:BOILERPLATE:CUSTOMER-INFO-PAGE:v1 ===

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useConfig,
  updateCustomerConfig,
  uploadLogoFile,
  SettingsHeader,
  SettingsFooter,
} from '@sluitstraat/core';

interface CustomerFormData {
  name: string;
  tagline: string;
  email: string;
  phone: string;
  website: string;
  logo: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  backgroundColor: string;
  textColor: string;
  fontFamily: string;
  customCSS: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  website?: string;
  primaryColor?: string;
  secondaryColor?: string;
}

const CustomerInformationPage: React.FC = () => {
  const { config, reloadConfig } = useConfig();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  const [formData, setFormData] = useState<CustomerFormData>({
    name: config.customer?.name || '',
    tagline: config.texts?.general?.tagline || config.customer?.tagline || '',
    email: config.customer?.contact?.email || '',
    phone: config.customer?.contact?.phone?.main || '',
    website: config.customer?.contact?.website || '',
    logo: config.customer?.logo || '',
    primaryColor: config.branding?.primaryColor || '#056666',
    secondaryColor: config.branding?.secondaryColor || '#F51C17',
    accentColor: config.branding?.accentColor || '#09B4B4',
    backgroundColor: config.branding?.backgroundColor || '#FFFFFF',
    textColor: config.branding?.textColor || '#373F47',
    fontFamily: config.branding?.fontFamily || 'Montserrat, Helvetica, Arial, sans-serif',
    customCSS: config.branding?.customCSS || '',
  });

  const [errors, setErrors] = useState<FormErrors>({});

  useEffect(() => {
    if (formData.logo) {
      setLogoPreview(formData.logo);
    }
  }, [formData.logo]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Customer name is required';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (formData.website && !/^https?:\/\/.+\..+/.test(formData.website)) {
      newErrors.website = 'Please enter a valid website URL (e.g., https://example.com)';
    }

    const hexColorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    if (!hexColorRegex.test(formData.primaryColor)) {
      newErrors.primaryColor = 'Please enter a valid hex color (e.g., #056666)';
    }

    if (formData.secondaryColor && !hexColorRegex.test(formData.secondaryColor)) {
      newErrors.secondaryColor = 'Please enter a valid hex color (e.g., #F51C17)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      setNotification({ type: 'error', message: 'Logo file size must be less than 10MB' });
      e.target.value = '';
      return;
    }

    // Show preview immediately while uploading
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Upload to backend
    setNotification({ type: 'success', message: 'Uploading logo...' });
    const logoPath = await uploadLogoFile(file);

    if (logoPath) {
      setFormData({ ...formData, logo: logoPath });
      setNotification({ type: 'success', message: 'Logo uploaded successfully!' });
    } else {
      setNotification({ type: 'error', message: 'Failed to upload logo. Please try again.' });
    }

    // Reset input to allow selecting the same file again
    e.target.value = '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      setNotification({ type: 'error', message: 'Please fix the errors before saving' });
      return;
    }

    setIsSubmitting(true);
    setNotification(null);

    try {
      const success = await updateCustomerConfig({
        name: formData.name,
        tagline: formData.tagline,
        email: formData.email,
        phone: formData.phone,
        website: formData.website,
        logo: formData.logo,
        primaryColor: formData.primaryColor,
        secondaryColor: formData.secondaryColor,
        accentColor: formData.accentColor,
        backgroundColor: formData.backgroundColor,
        textColor: formData.textColor,
        fontFamily: formData.fontFamily,
        customCSS: formData.customCSS,
      });

      if (success) {
        setNotification({ type: 'success', message: 'Customer information saved successfully!' });

        // Reload the config to reflect the changes
        try {
          await reloadConfig();
        } catch (configError) {
          // Continue with navigation even if reload fails
        }

        // Navigate back to setup page
        setTimeout(() => {
          navigate('/settings', { replace: true });
        }, 1500);
      } else {
        setNotification({ type: 'error', message: 'Failed to save customer information. Please try again.' });
      }
    } catch (error) {
      setNotification({ type: 'error', message: 'An error occurred while saving. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/settings');
  };

  return (
    <>
      <SettingsHeader />
      <div style={styles.container}>
        <div style={styles.content}>
        <header style={styles.header}>
          <button onClick={handleCancel} style={styles.backButton}>
            ← Back to Setup
          </button>
          <h1 style={styles.title}>Customer Information</h1>
          <p style={styles.subtitle}>Configure your customer details and branding</p>
        </header>

        {notification && (
          <div style={{
            ...styles.notification,
            ...(notification.type === 'success' ? styles.notificationSuccess : styles.notificationError)
          }}>
            <span style={styles.notificationIcon}>
              {notification.type === 'success' ? '✓' : '⚠'}
            </span>
            {notification.message}
          </div>
        )}

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Basic Information</h2>

            <div style={styles.formGroup}>
              <label style={styles.label}>
                Customer Name <span style={styles.required}>*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                style={{
                  ...styles.input,
                  ...(errors.name ? styles.inputError : {})
                }}
                placeholder="e.g., VeloVerzeker"
              />
              {errors.name && <span style={styles.errorText}>{errors.name}</span>}
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Tagline</label>
              <input
                type="text"
                value={formData.tagline}
                onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
                style={styles.input}
                placeholder="e.g., Insurance made simple"
              />
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Logo</h2>

            <div style={styles.logoSection}>
              <div style={styles.logoPreviewContainer}>
                {logoPreview ? (
                  <img src={logoPreview} alt="Logo preview" style={styles.logoPreview} />
                ) : (
                  <div style={styles.logoPlaceholder}>
                    <span style={styles.logoPlaceholderText}>No logo uploaded</span>
                  </div>
                )}
              </div>

              <div style={styles.logoUploadSection}>
                <div>
                  <input
                    id="logo-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    style={styles.fileInput}
                  />
                  <label style={styles.fileLabel} htmlFor="logo-upload">
                    <span style={styles.fileLabelText}>Choose Logo File</span>
                  </label>
                </div>
                <p style={styles.fileHint}>PNG, JPG, or SVG. Max 10MB</p>

                <div style={styles.divider}>or</div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Logo URL</label>
                  <input
                    type="text"
                    value={formData.logo}
                    onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
                    style={styles.input}
                    placeholder="https://example.com/logo.png"
                  />
                </div>
              </div>
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Contact Information</h2>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  style={{
                    ...styles.input,
                    ...(errors.email ? styles.inputError : {})
                  }}
                  placeholder="contact@example.com"
                />
                {errors.email && <span style={styles.errorText}>{errors.email}</span>}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  style={styles.input}
                  placeholder="+31 20 123 4567"
                />
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Website</label>
              <input
                type="url"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                style={{
                  ...styles.input,
                  ...(errors.website ? styles.inputError : {})
                }}
                placeholder="https://www.example.com"
              />
              {errors.website && <span style={styles.errorText}>{errors.website}</span>}
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Branding & Styling</h2>

            <h3 style={styles.subsectionTitle}>Colors</h3>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Primary Color <span style={styles.required}>*</span>
                </label>
                <div style={styles.colorInputGroup}>
                  <input
                    type="color"
                    value={formData.primaryColor}
                    onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                    style={styles.colorPicker}
                  />
                  <input
                    type="text"
                    value={formData.primaryColor}
                    onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                    style={{
                      ...styles.colorInput,
                      ...(errors.primaryColor ? styles.inputError : {})
                    }}
                    placeholder="#056666"
                  />
                </div>
                {errors.primaryColor && <span style={styles.errorText}>{errors.primaryColor}</span>}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Secondary Color</label>
                <div style={styles.colorInputGroup}>
                  <input
                    type="color"
                    value={formData.secondaryColor}
                    onChange={(e) => setFormData({ ...formData, secondaryColor: e.target.value })}
                    style={styles.colorPicker}
                  />
                  <input
                    type="text"
                    value={formData.secondaryColor}
                    onChange={(e) => setFormData({ ...formData, secondaryColor: e.target.value })}
                    style={{
                      ...styles.colorInput,
                      ...(errors.secondaryColor ? styles.inputError : {})
                    }}
                    placeholder="#F51C17"
                  />
                </div>
                {errors.secondaryColor && <span style={styles.errorText}>{errors.secondaryColor}</span>}
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Accent Color</label>
                <div style={styles.colorInputGroup}>
                  <input
                    type="color"
                    value={formData.accentColor}
                    onChange={(e) => setFormData({ ...formData, accentColor: e.target.value })}
                    style={styles.colorPicker}
                  />
                  <input
                    type="text"
                    value={formData.accentColor}
                    onChange={(e) => setFormData({ ...formData, accentColor: e.target.value })}
                    style={styles.colorInput}
                    placeholder="#ff9800"
                  />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Background Color</label>
                <div style={styles.colorInputGroup}>
                  <input
                    type="color"
                    value={formData.backgroundColor}
                    onChange={(e) => setFormData({ ...formData, backgroundColor: e.target.value })}
                    style={styles.colorPicker}
                  />
                  <input
                    type="text"
                    value={formData.backgroundColor}
                    onChange={(e) => setFormData({ ...formData, backgroundColor: e.target.value })}
                    style={styles.colorInput}
                    placeholder="#ffffff"
                  />
                </div>
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Text Color</label>
              <div style={styles.colorInputGroup}>
                <input
                  type="color"
                  value={formData.textColor}
                  onChange={(e) => setFormData({ ...formData, textColor: e.target.value })}
                  style={styles.colorPicker}
                />
                <input
                  type="text"
                  value={formData.textColor}
                  onChange={(e) => setFormData({ ...formData, textColor: e.target.value })}
                  style={styles.colorInput}
                  placeholder="#1f2937"
                />
              </div>
            </div>

            <h3 style={styles.subsectionTitle}>Typography</h3>
            <div style={styles.formGroup}>
              <label style={styles.label}>Font Family</label>
              <select
                value={formData.fontFamily}
                onChange={(e) => setFormData({ ...formData, fontFamily: e.target.value })}
                style={styles.select}
              >
                <option value="Inter, system-ui, sans-serif">Inter (Default)</option>
                <option value="'Helvetica Neue', Helvetica, Arial, sans-serif">Helvetica</option>
                <option value="Arial, sans-serif">Arial</option>
                <option value="'Segoe UI', Tahoma, Geneva, Verdana, sans-serif">Segoe UI</option>
                <option value="'Roboto', sans-serif">Roboto</option>
                <option value="'Open Sans', sans-serif">Open Sans</option>
                <option value="'Lato', sans-serif">Lato</option>
                <option value="'Montserrat', sans-serif">Montserrat</option>
                <option value="Georgia, 'Times New Roman', serif">Georgia (Serif)</option>
                <option value="'Courier New', monospace">Courier New (Monospace)</option>
              </select>
            </div>

            <h3 style={styles.subsectionTitle}>Custom CSS (Advanced)</h3>
            <div style={styles.formGroup}>
              <label style={styles.label}>Custom CSS Rules</label>
              <textarea
                value={formData.customCSS}
                onChange={(e) => setFormData({ ...formData, customCSS: e.target.value })}
                style={styles.textarea}
                rows={6}
                placeholder={`:root {\n  --custom-property: value;\n}\n\n.custom-class {\n  /* your styles */\n}`}
              />
              <p style={styles.hint}>Add custom CSS rules to override default styles. Use with caution.</p>
            </div>
          </div>

          <div style={styles.actionBar}>
            <button
              type="button"
              onClick={handleCancel}
              style={styles.cancelButton}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                ...styles.saveButton,
                ...(isSubmitting ? styles.saveButtonDisabled : {})
              }}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
    <SettingsFooter />
  </>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#F5F5F5',
    padding: '32px 20px',
  },
  content: {
    maxWidth: '900px',
    margin: '0 auto',
  },
  header: {
    marginBottom: '32px',
  },
  backButton: {
    padding: '8px 16px',
    backgroundColor: 'transparent',
    color: '#6b7280',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600' as const,
    cursor: 'pointer',
    marginBottom: '16px',
    transition: 'color 0.2s',
  },
  title: {
    margin: '0 0 8px 0',
    fontSize: '32px',
    fontWeight: '700' as const,
    color: '#1f2937',
  },
  subtitle: {
    margin: 0,
    fontSize: '16px',
    color: '#6b7280',
  },
  notification: {
    padding: '16px 20px',
    borderRadius: '8px',
    marginBottom: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '14px',
    fontWeight: '600' as const,
  },
  notificationSuccess: {
    backgroundColor: '#d1fae5',
    color: '#065f46',
    border: '2px solid #10b981',
  },
  notificationError: {
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    border: '2px solid #ef4444',
  },
  notificationIcon: {
    fontSize: '18px',
    fontWeight: '700' as const,
  },
  form: {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    padding: '32px',
  },
  section: {
    marginBottom: '32px',
    paddingBottom: '32px',
    borderBottom: '1px solid #e5e7eb',
  },
  sectionTitle: {
    margin: '0 0 20px 0',
    fontSize: '18px',
    fontWeight: '600' as const,
    color: '#1f2937',
  },
  formGroup: {
    marginBottom: '20px',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: '600' as const,
    color: '#374151',
  },
  required: {
    color: '#ef4444',
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    fontFamily: 'inherit',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box' as const,
  },
  inputError: {
    borderColor: '#ef4444',
  },
  errorText: {
    display: 'block',
    marginTop: '6px',
    fontSize: '13px',
    color: '#ef4444',
  },
  subsectionTitle: {
    margin: '24px 0 16px 0',
    fontSize: '15px',
    fontWeight: '600' as const,
    color: '#4b5563',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    borderBottom: '2px solid #e5e7eb',
    paddingBottom: '8px',
  },
  select: {
    width: '100%',
    padding: '10px 14px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    fontFamily: 'inherit',
    backgroundColor: 'white',
    cursor: 'pointer',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box' as const,
  },
  textarea: {
    width: '100%',
    padding: '10px 14px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '13px',
    fontFamily: "'Courier New', monospace",
    resize: 'vertical' as const,
    transition: 'border-color 0.2s',
    boxSizing: 'border-box' as const,
    lineHeight: '1.6',
  },
  hint: {
    margin: '6px 0 0 0',
    fontSize: '12px',
    color: '#9ca3af',
    fontStyle: 'italic' as const,
  },
  logoSection: {
    display: 'grid',
    gridTemplateColumns: '200px 1fr',
    gap: '24px',
    alignItems: 'start',
  },
  logoPreviewContainer: {
    width: '200px',
    height: '200px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F5F5F5',
    borderRadius: '8px',
    border: '2px solid #e5e7eb',
  },
  logoPreview: {
    maxWidth: '180px',
    maxHeight: '180px',
    objectFit: 'contain' as const,
  },
  logoPlaceholder: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2px dashed #d1d5db',
    borderRadius: '8px',
  },
  logoPlaceholderText: {
    fontSize: '14px',
    color: '#9ca3af',
  },
  logoUploadSection: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
  },
  fileLabel: {
    display: 'inline-block',
    padding: '10px 20px',
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    border: '1px solid #2563eb',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600' as const,
    cursor: 'pointer',
    textAlign: 'center' as const,
    transition: 'background-color 0.2s',
    width: 'fit-content',
    userSelect: 'none' as const,
  },
  fileLabelText: {
    pointerEvents: 'none' as const,
  },
  fileInput: {
    display: 'none',
  },
  fileHint: {
    margin: '0',
    fontSize: '13px',
    color: '#6b7280',
  },
  divider: {
    textAlign: 'center' as const,
    fontSize: '13px',
    color: '#9ca3af',
    fontWeight: '600' as const,
    position: 'relative' as const,
    padding: '8px 0',
  },
  colorInputGroup: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
  },
  colorPicker: {
    width: '60px',
    height: '44px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  colorInput: {
    flex: 1,
    padding: '10px 14px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    fontFamily: 'monospace',
    transition: 'border-color 0.2s',
  },
  actionBar: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    paddingTop: '24px',
    marginTop: '8px',
  },
  cancelButton: {
    padding: '12px 24px',
    backgroundColor: '#f3f4f6',
    color: '#374151',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600' as const,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  saveButton: {
    padding: '12px 32px',
    backgroundColor: 'var(--primary-color, #056666)',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600' as const,
    cursor: 'pointer',
    transition: 'opacity 0.2s',
  },
  saveButtonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
};

export default CustomerInformationPage;
